import { useState } from "react";
import axios from "axios";
import { z } from "zod";
import { createTenderSchema } from "../app/features/tender/tender-schema";
import { createTenderQuotationSchema } from "../app/features/tender/components/view/tenderQuotationSchema";
import { getData } from "@/lib/utils";
import api from "@/store/api";
import { useDispatch } from "react-redux";
import { getCategories } from "@/store/authSlice";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { showToast } from "@/app/global-components/toaster";

const useOffer = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const TenderSchema = createTenderSchema(t as (key: string) => string);
  const tenderQuotationSchema = createTenderQuotationSchema(
    t as (key: string) => string
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveingLoading, setisSaveingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();

  const createOffer = async (values: z.infer<typeof TenderSchema>) => {
    setisSaveingLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.post("/web/tenders/upsert", values, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setisSaveingLoading(false);
      return response.data;
    } catch (err) {
      setisSaveingLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.creatingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const updateOfferd = async (values: z.infer<typeof TenderSchema>) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.post("/web/tenders/upsert", values, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.updatingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const sendOffer = async (id: string, status: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.post(
        "/web/tenders/send",
        { id, status },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      showToast(t("Alert.OfferSubmitted"), "success");

      window.location.reload();

      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("Alert.OfferSendError"));
        showToast(
          err.response?.data?.message || t("Alert.OfferSendError"),
          "warning"
        );
      } else {
        setError(t("Alert.UnexpectedError"));
        showToast(t("Alert.UnexpectedError"), "warning");
      }
      throw err;
    }
  };

  const updateOffer = async (
    id: string,
    values: z.infer<typeof TenderSchema>
  ) => {
    setisSaveingLoading(true);
    setError(null);
    try {
      const response = await api.put(`/api/offers/${id}`, values);
      setisSaveingLoading(false);
      return response.data;
    } catch (err) {
      setisSaveingLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.offerUpdateFailed")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };

  const getOffer = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/web/tenders/get?id=${id}`);
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(err.response?.data?.message || t("alerts.error.fetchOffer"));
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const deleteOffer = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const Token = getData("tokens")?.accessToken;

      await api.delete(`/web/tenders/delete?id=${id}`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });

      setIsLoading(false);
      return "Offer deleted successfully";
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.deletingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getOffers = async (Status: String) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/web/tenders/getAll?pageSize=10&pageNumber=1&withPaging=false&status=${Status}`
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingOffers")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getMyOffers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get(
        `/web/tenders/getMy?pageSize=10&pageNumber=1&withPaging=false`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          // Offer not found, return null instead of throwing an error
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingOffers")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getcategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get(
        `/web/categories/getAll?pageSize=10&pageNumber=1&withPaging=false`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      dispatch(getCategories(response.data.data));
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingCategories")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getParentCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get(
        `/web/categories/getAll?pageSize=10&pageNumber=1&withPaging=false&parentId=null`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      dispatch(getCategories(response.data.data));
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingCategories")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const getcompanies = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get(
        `/web/companies/getAll?pageSize=10&pageNumber=1&withPaging=false`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingCompanies")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const sendOffertRequest = async (
    values: z.infer<typeof tenderQuotationSchema>
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const { customDeliveryMethod, ...filteredValues } = values;
      const response = await api.post(
        "/web/tenderQuotations/upsert",
        filteredValues,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.sendingOffer"));
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const refuseQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/cancelled`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.refusingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const acceptQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/accept`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.acceptingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const PaymentRequestQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/pay/request`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || "Error updating offer");
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const SendRequestQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/send/request`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.sendingOffer"));
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const SendQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/send`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.sendingOffer"));
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  const ReceiveQuatation = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.put(
        `/web/tenderQuotations/receive`,
        { quatationID: id },
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.receivingOffer")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  };
  return {
    refuseQuatation,
    sendOffer,
    ReceiveQuatation,
    SendQuatation,
    SendRequestQuatation,
    PaymentRequestQuatation,
    acceptQuatation,
    createOffer,
    deleteOffer,
    updateOfferd,
    updateOffer,
    getOffer,
    getOffers,
    getcategories,
    getcompanies,
    sendOffertRequest,
    getMyOffers,
    getParentCategories,
    isSaveingLoading,
    isLoading,
    error,
  };
};

export default useOffer;
